import axios from "../utils/api-client";

export interface ShiftInstance {
	shiftId: string
	startDateTime: string
	endDateTime: string
	locationId: number
	workcenterId: number
	shiftPatternId: number
	shiftPatternName?: string
}

export interface ShiftPattern {
	id: number
	name: string
	hexColor?: string
	startTime?: string
	endTime?: string
}

// Predefined colors for shifts (pastel, accessible colors)
const SHIFT_COLORS: string[] = [
	"#B3E5FC", // Light Blue (Shift 1 - Morning)
	"#C8E6C9", // Light Green (Shift 2 - Afternoon)
	"#FFE0B2", // Light Orange (Shift 3 - Evening)
	"#E1BEE7", // Light Purple (Shift 4 - Night)
	"#FFCDD2", // Light Red
	"#B2DFDB", // Light Teal
	"#F0F4C3", // Light Lime
	"#D7CCC8", // Light Brown
];

/**
 * A flex/OT shift to open up, as accepted by Shifts/Create. Mirrors the
 * server's CreateShiftRequest; CompanyId is stamped server-side from the caller.
 */
export interface CreateShiftPayload {
	startDateTime: string
	endDateTime: string
	jobId?: number | null
	workCenterId?: number | null
	numberOfWorkersNeeded: number
	shiftPremium?: string
	shiftNote?: string
	createdBy: number
	isPublished?: boolean
	/**
	 * Required, even when empty. Shifts/Create walks this list without a null
	 * check (ShiftRepository.CreateShifts), so omitting it throws a
	 * NullReferenceException server-side and the call returns a bare 500.
	 */
	shiftSkills: { skillId: number; skillLevelId: number }[]
}

export interface CreatedShift {
	id: number
}

export const shiftsApi = {
	/**
	 * Get shift instances for a date range
	 */
	/**
	 * Create the shifts as drafts. Nothing is offered to anyone until
	 * publishShifts runs — the two steps are deliberately separate.
	 */
	createShifts: async (payloads: CreateShiftPayload[]): Promise<CreatedShift[]> => {
		const response = await axios.post<CreatedShift[]>(
			`${process.env.REACT_APP_API_END_POINT}/Shifts/Create`,
			payloads
		);
		return response.data;
	},

	/**
	 * Publish drafted shifts — THIS IS WHAT SENDS THEM TO WORKERS. The endpoint
	 * takes a CSV of ids as a raw JSON string body, not an array.
	 */
	publishShifts: async (shiftIds: number[]): Promise<void> => {
		await axios.post(
			`${process.env.REACT_APP_API_END_POINT}/Shifts/Publish`,
			JSON.stringify(shiftIds.join(",")),
			{ headers: { "Content-Type": "application/json" } }
		);
	},

	/** Unclaimed spots per shift, for the fill status on published requests. */
	getSpotsLeft: async (shiftIds: number[]): Promise<Record<number, number>> => {
		const response = await axios.post<{ shiftId: number; spotsLeft: number }[]>(
			`${process.env.REACT_APP_API_END_POINT}/Shifts/SpotsLeft`,
			shiftIds
		);
		const map: Record<number, number> = {}
		;(response.data || []).forEach((row) => {
			map[row.shiftId] = row.spotsLeft;
		});
		return map;
	},

	getShiftsForDateRange: async (
		startDate: string,
		endDate: string
	): Promise<ShiftInstance[]> => {
		try {
			const response = await axios.get<ShiftInstance[]>(
				`${process.env.REACT_APP_API_END_POINT}/FTShiftsView/GetFTShifts`,
				{
					params: {
						startDate,
						endDate,
					},
				}
			);
			return response.data || [];
		} catch (error) {
			console.error("Failed to fetch shifts:", error);
			return [];
		}
	},

	/**
	 * Get unique shift patterns from shift instances
	 */
	getUniqueShiftPatterns: (shifts: ShiftInstance[]): ShiftPattern[] => {
		if (!shifts || shifts.length === 0) {
			return [];
		}
		
		const patternMap = new Map<number, ShiftPattern>();
		
		shifts.forEach((shift) => {
			if (shift && shift.shiftPatternId && !patternMap.has(shift.shiftPatternId)) {
				// Parse start and end times from the ISO string
				const startDate = new Date(shift.startDateTime);
				const endDate = new Date(shift.endDateTime);
				
				patternMap.set(shift.shiftPatternId, {
					id: shift.shiftPatternId,
					name: shift.shiftPatternName || `Shift ${shift.shiftPatternId}`,
					hexColor: SHIFT_COLORS[patternMap.size % SHIFT_COLORS.length],
					startTime: `${startDate.getHours().toString().padStart(2, "0")}:${startDate.getMinutes().toString().padStart(2, "0")}`,
					endTime: `${endDate.getHours().toString().padStart(2, "0")}:${endDate.getMinutes().toString().padStart(2, "0")}`,
				});
			}
		});
		
		return Array.from(patternMap.values()).sort((a, b) => a.name.localeCompare(b.name));
	},

	/**
	 * Get color for a shift pattern
	 */
	getShiftColor: (patternId: number, patterns: ShiftPattern[]): string => {
		const pattern = patterns.find((p) => p.id === patternId);
		return pattern?.hexColor || SHIFT_COLORS[0];
	},

	/**
	 * Group shifts by pattern for a specific date
	 */
	getShiftsForDate: (shifts: ShiftInstance[], date: Date): ShiftInstance[] => {
		if (!shifts || shifts.length === 0 || !date) {
			return [];
		}
		return shifts.filter((shift) => {
			if (!shift || !shift.startDateTime) return false;
			const shiftDate = new Date(shift.startDateTime);
			return (
				shiftDate.getFullYear() === date.getFullYear() &&
				shiftDate.getMonth() === date.getMonth() &&
				shiftDate.getDate() === date.getDate()
			);
		});
	},

	/**
	 * Get the hour range covered by a shift (in local time)
	 */
	getShiftHourRange: (shift: ShiftInstance): { startHour: number; endHour: number } => {
		if (!shift || !shift.startDateTime || !shift.endDateTime) {
			return { startHour: 0, endHour: 0 };
		}
		const startDate = new Date(shift.startDateTime);
		const endDate = new Date(shift.endDateTime);
		
		return {
			startHour: startDate.getHours() + startDate.getMinutes() / 60,
			endHour: endDate.getHours() + endDate.getMinutes() / 60,
		};
	},
};

export default shiftsApi;


